// Dados do processo que costumam faltar no quadro "Informação do negócio":
// escritura realizada (data, hora, local, notário), financiamento (valor e banco) e faturas/recibos.
// O lápis transforma as linhas em campos, guarda no negócio e mostra o que ainda falta preencher.
import { useState } from "react";
import { Pencil, Check, AlertTriangle } from "lucide-react";
import { ds } from "./desenho";
import { eurTxt } from "./RecebimentoCalculo";

const num = (v) => Number(v || 0);
const dataPT = (d) => (d ? new Date(String(d).slice(0, 10) + "T12:00:00").toLocaleDateString("pt-PT") : "");
const campo = { fontSize: 13, border: "1px solid #D5DDEE", borderRadius: 8, padding: "6px 8px", width: "100%", fontFamily: "inherit", background: "#F4F7FD", boxSizing: "border-box" };
const btn = { borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit" };

const valoresIniciais = (n) => ({
  data_escritura_realizada: n.data_escritura_realizada ? String(n.data_escritura_realizada).slice(0, 10) : "",
  hora_escritura_realizada: n.hora_escritura_realizada ? String(n.hora_escritura_realizada).slice(0, 5) : "",
  local_escritura_realizada: n.local_escritura_realizada || "",
  notario_escritura: n.notario_escritura || "",
  notario_morada: n.notario_morada || "",
  notario_telefone: n.notario_telefone || "",
  notas_escritura: n.notas_escritura || "",
  financiamento: num(n.valor_financiamento) > 0 || n.teve_financiamento === true ? "sim" : n.teve_financiamento === false ? "nao" : "",
  valor_financiamento: n.valor_financiamento ? String(n.valor_financiamento) : "",
  banco_financiamento: n.banco_financiamento || "",
});

// Campos do CPCV que espelham os dados do processo (CPCV <- negócio)
const NOMES_CPCV = { escritura_data: "data da escritura", escritura_hora: "hora", escritura_local: "local", notario_nome: "notário", notario_morada: "morada do notário", notario_telefone: "telefone do notário", escritura_notas: "notas", banco_credito: "banco" };
const norm = (k, x) => (x == null ? "" : k === "escritura_data" ? String(x).slice(0, 10) : k === "escritura_hora" ? String(x).slice(0, 5) : String(x).trim());

// O que o negócio quer escrever no CPCV: só valores preenchidos (nunca apaga nada no CPCV)
export function dadosParaCpcv(d) {
  const o = {
    escritura_data: d.data_escritura_realizada, escritura_hora: d.hora_escritura_realizada, escritura_local: d.local_escritura_realizada,
    notario_nome: d.notario_escritura, notario_morada: d.notario_morada, notario_telefone: d.notario_telefone, escritura_notas: d.notas_escritura,
    banco_credito: d.banco_financiamento,
  };
  const r = {};
  Object.keys(o).forEach((k) => { if (norm(k, o[k])) r[k] = typeof o[k] === "string" && k !== "escritura_data" && k !== "escritura_hora" ? o[k].trim() : o[k]; });
  if (d.teve_financiamento === true) r.recurso_credito = true;
  return r;
}

// Campos em que o CPCV já tem um valor diferente do que vai ser gravado
export function diferencasCpcv(novo, cpcv) {
  return Object.keys(novo).filter((k) => k !== "recurso_credito" && norm(k, cpcv[k]) && norm(k, cpcv[k]) !== norm(k, novo[k])).map((k) => NOMES_CPCV[k] || k);
}

// Lista sem repetidos dos números de fatura ou de recibo registados nas linhas de recebimento
export const numerosRegistados = (recs, chave) => Array.from(new Set((recs || []).map((r) => (r[chave] || "").trim()).filter(Boolean)));

export default function DadosProcesso({ neg, recs = [], podeEditar = false, supabase, onGuardado, onAviso }) {
  const [editar, setEditar] = useState(false);
  const [v, setV] = useState(() => valoresIniciais(neg));
  const [erro, setErro] = useState("");
  const [aGravar, setAGravar] = useState(false);
  const [conflito, setConflito] = useState(null); // { dados, cpcv, novo, campos }: o CPCV tem dados diferentes
  const mudar = (k, x) => { setV((p) => ({ ...p, [k]: x })); setErro(""); };

  const faturas = numerosRegistados(recs, "numero_fatura");
  const recibos = numerosRegistados(recs, "numero_recibo");
  const comFin = num(neg.valor_financiamento) > 0 || neg.teve_financiamento === true;
  const semFin = neg.teve_financiamento === false && !(num(neg.valor_financiamento) > 0);

  const linhas = [
    ["Escritura realizada", neg.data_escritura_realizada ? dataPT(neg.data_escritura_realizada) + (neg.hora_escritura_realizada ? " às " + String(neg.hora_escritura_realizada).slice(0, 5) : "") : null],
    ["Local da escritura", neg.local_escritura_realizada],
    ["Notário", [neg.notario_escritura, neg.notario_telefone].filter(Boolean).join(" · ") || null],
    ["Financiamento", semFin ? "Sem financiamento" : comFin ? (num(neg.valor_financiamento) > 0 ? eurTxt(neg.valor_financiamento) : "Sim, valor por indicar") : null],
    ["Banco", semFin ? "Não aplicável" : neg.banco_financiamento],
    ["Faturas", faturas.length ? faturas.join(", ") : null],
    ["Recibos", recibos.length ? recibos.join(", ") : null],
  ];
  const faltam = linhas.filter(([, x]) => !x).length;

  const guardar = async (substituirCpcv) => {
    if (v.financiamento === "sim" && v.valor_financiamento !== "" && !(num(String(v.valor_financiamento).replace(",", ".")) >= 0)) return setErro("O valor financiado não é válido.");
    const valor = num(String(v.valor_financiamento).replace(",", "."));
    const sim = v.financiamento === "sim";
    const dados = {
      data_escritura_realizada: v.data_escritura_realizada || null,
      hora_escritura_realizada: v.hora_escritura_realizada || null,
      local_escritura_realizada: v.local_escritura_realizada.trim() || null,
      notario_escritura: v.notario_escritura.trim() || null,
      notario_morada: v.notario_morada.trim() || null,
      notario_telefone: v.notario_telefone.trim() || null,
      notas_escritura: v.notas_escritura.trim() || null,
      teve_financiamento: v.financiamento === "" ? null : sim,
      valor_financiamento: sim && valor > 0 ? valor : null,
      banco_financiamento: sim ? v.banco_financiamento.trim() || null : null,
      updated_at: new Date().toISOString(),
    };
    setAGravar(true);
    // 1. CPCV do negócio: se tiver dados diferentes, pergunta antes de os substituir
    let cpcv = null;
    try {
      const r = await supabase.from("formularios_cpcv").select("*").eq("negocio_id", neg.id).is("deleted_at", null).order("created_at", { ascending: false }).limit(1);
      cpcv = (r && r.data && r.data[0]) || null;
    } catch (e) { cpcv = null; }
    const novo = dadosParaCpcv(dados);
    const campos = cpcv ? diferencasCpcv(novo, cpcv) : [];
    if (cpcv && campos.length && substituirCpcv === undefined) { setAGravar(false); setConflito({ campos }); return; }
    // 2. Negócio
    const { error } = await supabase.from("negocios").update(dados).eq("id", neg.id);
    if (error) { setAGravar(false); return setErro("Não foi possível guardar: " + error.message); }
    // 3. CPCV: preenche o que está vazio e, se o utilizador aceitou, substitui o que era diferente
    let cpcvTocado = false;
    if (cpcv && Object.keys(novo).length) {
      const aplicar = {};
      Object.keys(novo).forEach((k) => {
        const antes = k === "recurso_credito" ? cpcv[k] === true : norm(k, cpcv[k]);
        if (k === "recurso_credito") { if (!antes) aplicar[k] = true; return; }
        if (!antes || (substituirCpcv === true && norm(k, cpcv[k]) !== norm(k, novo[k]))) aplicar[k] = novo[k];
      });
      // o valor a financiar do CPCV é o previsto, por isso só se preenche se estiver vazio
      if (dados.valor_financiamento && !(Number(cpcv.valor_a_financiar) > 0)) aplicar.valor_a_financiar = dados.valor_financiamento;
      if (Object.keys(aplicar).length) {
        const r2 = await supabase.from("formularios_cpcv").update({ ...aplicar, updated_at: new Date().toISOString() }).eq("id", cpcv.id);
        if (r2 && r2.error) { setAGravar(false); if (onGuardado) await onGuardado(); setEditar(false); setConflito(null); if (onAviso) onAviso("Guardado no negócio, mas não foi possível atualizar o CPCV: " + r2.error.message, "error"); return; }
        cpcvTocado = true;
      }
    }
    setAGravar(false);
    setConflito(null);
    if (onGuardado) await onGuardado();
    setEditar(false);
    if (onAviso) onAviso(cpcvTocado ? "Dados do processo guardados no negócio e no CPCV." : "Dados do processo guardados.");
  };
  const cancelar = () => { setV(valoresIniciais(neg)); setErro(""); setConflito(null); setEditar(false); };

  const rot = { fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" };
  const celula = (l, x, extra) => (
    <label style={{ display: "grid", gap: 3, ...extra }}><span style={rot}>{l}</span>{x}</label>
  );

  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #f1f1ee" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
        <div style={{ ...rot, fontSize: 10.5, letterSpacing: "0.06em" }}>Escritura, financiamento e faturas</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {!editar && faltam > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, color: ds.atencao, fontWeight: 600 }}><AlertTriangle size={14} aria-hidden="true" />{faltam === 1 ? "falta 1 dado" : "faltam " + faltam + " dados"}</span>}
          {!editar && podeEditar && <button onClick={() => setEditar(true)} title="Editar os dados em falta" aria-label="Editar os dados da escritura e do financiamento" style={{ ...btn, padding: "5px 9px", background: "#fff", color: ds.navy9, border: "1px solid #D5DDEE" }}><Pencil size={14} aria-hidden="true" /></button>}
        </div>
      </div>

      {!editar && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
          {linhas.map(([l, x]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: "1px solid #f5f5f3" }}>
              <span style={rot}>{l}</span>
              <span style={{ fontSize: 12.5, fontWeight: 600, textAlign: "right", maxWidth: "62%", color: x ? "#1a1a2e" : ds.atencao }}>{x || "por preencher"}</span>
            </div>
          ))}
          {neg.notas_escritura && <div style={{ gridColumn: "1 / -1", padding: "7px 0", fontSize: 12.5, color: "#4b5563" }}><span style={rot}>Notas da escritura </span>{neg.notas_escritura}</div>}
          <div style={{ gridColumn: "1 / -1", padding: "7px 0", fontSize: 12, color: "#6b7280" }}>Os números das faturas e dos recibos editam-se nas linhas dos recebimentos, para ficarem sempre iguais nos dois sítios.</div>
        </div>
      )}

      {editar && (
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
            {celula("Data da escritura", <input type="date" aria-label="Data da escritura" value={v.data_escritura_realizada} onChange={(e) => mudar("data_escritura_realizada", e.target.value)} style={campo} />)}
            {celula("Hora", <input type="time" aria-label="Hora da escritura" value={v.hora_escritura_realizada} onChange={(e) => mudar("hora_escritura_realizada", e.target.value)} style={campo} />)}
            {celula("Local da escritura", <input type="text" aria-label="Local da escritura" value={v.local_escritura_realizada} onChange={(e) => mudar("local_escritura_realizada", e.target.value)} style={campo} />)}
            {celula("Notário", <input type="text" aria-label="Notário" value={v.notario_escritura} onChange={(e) => mudar("notario_escritura", e.target.value)} style={campo} />)}
            {celula("Morada do notário", <input type="text" aria-label="Morada do notário" value={v.notario_morada} onChange={(e) => mudar("notario_morada", e.target.value)} style={campo} />)}
            {celula("Telefone do notário", <input type="text" aria-label="Telefone do notário" value={v.notario_telefone} onChange={(e) => mudar("notario_telefone", e.target.value)} style={campo} />)}
            {celula("Financiamento", <select aria-label="Financiamento" value={v.financiamento} onChange={(e) => mudar("financiamento", e.target.value)} style={campo}><option value="">Por indicar</option><option value="sim">Com financiamento</option><option value="nao">Sem financiamento</option></select>)}
            {v.financiamento === "sim" && celula("Valor financiado (€)", <input type="number" min="0" step="0.01" aria-label="Valor financiado" value={v.valor_financiamento} onChange={(e) => mudar("valor_financiamento", e.target.value)} style={campo} />)}
            {v.financiamento === "sim" && celula("Banco", <input type="text" aria-label="Banco" value={v.banco_financiamento} onChange={(e) => mudar("banco_financiamento", e.target.value)} style={campo} />)}
          </div>
          {celula("Notas da escritura", <textarea aria-label="Notas da escritura" value={v.notas_escritura} onChange={(e) => mudar("notas_escritura", e.target.value)} style={{ ...campo, minHeight: 54 }} />)}
          <div style={{ fontSize: 12, color: "#6b7280" }}>Estes dados gravam-se também no CPCV do negócio. As faturas e os recibos registam-se nas linhas dos recebimentos. Para marcar o negócio como concluído use o botão "Concluído".</div>
          {erro && <div role="alert" style={{ color: ds.falta, fontSize: 13, fontWeight: 600 }}>{erro}</div>}
          {conflito && (
            <div role="alert" style={{ background: ds.atencaoFundo, color: ds.atencao, borderRadius: 10, padding: "10px 14px", fontSize: 13, display: "grid", gap: 8 }}>
              <span style={{ fontWeight: 600 }}>O CPCV deste negócio já tem outros dados em: {conflito.campos.join(", ")}.</span>
              <span>Quer substituí-los pelos que acabou de escrever? Os campos vazios do CPCV preenchem-se sempre.</span>
              <span style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => guardar(true)} style={{ ...btn, background: "#1E3566", color: "#fff", border: "1px solid #1E3566" }}>Substituir no CPCV</button>
                <button onClick={() => guardar(false)} style={{ ...btn, background: "#fff", color: ds.navy9, border: "1px solid #D5DDEE" }}>Manter o CPCV como está</button>
              </span>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={cancelar} style={{ ...btn, background: "#fff", color: ds.navy9, border: "1px solid #D5DDEE" }}>Cancelar</button>
            <button onClick={() => guardar()} disabled={aGravar} style={{ ...btn, background: "#1E3566", color: "#fff", border: "1px solid #1E3566", opacity: aGravar ? 0.7 : 1 }}><Check size={15} aria-hidden="true" />{aGravar ? "A guardar..." : "Guardar"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
